import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ClassNameMap } from '@mui/styles';
import classnames from 'classnames';
import { Skeleton } from '@mui/material';
import {
  TimesheetLabel as Label,
  FormTimesheetLabel
} from 'modules/timesheet-labels/labelSchema';

import {
  useLabelsFormFieldContext,
  useTimesheetLabelSuggestionContext
} from 'modules/timesheet-labels';
import { useTrackRenderCounts } from 'modules/common/components/Render';
import { FetchSuggestionsHandlerProps } from 'modules/notes/addNote/hooks';
import { LabelType, TimeSignalAttribute } from '../../graphql/types';
import { mapFormFieldsToTimesheetLabels } from '../notes/common/utils';
import { TimesheetLabel } from './TimesheetLabel';
import { filterLabelsByType, isLabelType } from './hooks/utils/labelUtils';
import { TimesheetCommentLabel } from './TimesheetCommentLabel';
import { useIsSmartSuggestionSettingEnabled } from './hooks/useIsSmartSuggestionSettingEnabled';
import { TimeSheetMoreAction } from './TimeSheetMoreAction';

type StyleProps = {
  loading: boolean;
  classes?: ClassNameMap;
};
const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2.5, 1.5),
    backgroundColor: theme.palette.timesheetLabel.container,
    borderRadius: theme.spacing(0)
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.grey[700],
    padding: theme.spacing(0, 0, 0, 1),
    justifyContent: 'space-between'
  },
  title: {
    paddingLeft: theme.spacing(0)
  },
  blueTitle: {
    color: theme.palette.repliconBlue.main
  },
  labelsContainer: {
    display: ({ loading }: StyleProps) => (loading ? 'none' : 'flex'),
    flexFlow: 'wrap',
    padding: theme.spacing(1, 1, 0, 1)
  },
  commentLabelContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: theme.spacing(1, 0, 0, 0)
  },
  labelSkeleton: {
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    marginTop: theme.spacing(-1),
    background: theme.skelton?.color,
    height: theme.spacing(6),
    borderRadius: theme.spacing(2.75)
  }
}));

export type TimesheetLabelsProps = {
  fields: FormTimesheetLabel[];
  loading: boolean;
  attributes?: TimeSignalAttribute[] | undefined;
  onLabelChange?: (props: FetchSuggestionsHandlerProps) => void;
  classes?: ClassNameMap;
};

const TimesheetLabelsFC: React.FC<TimesheetLabelsProps> = ({
  fields = [],
  loading,
  attributes,
  onLabelChange,
  classes: overrideClasses
}: TimesheetLabelsProps) => {
  const { disableSuggestions, variant } = useTimesheetLabelSuggestionContext();
  const isSmartSuggestionEnabled =
    useIsSmartSuggestionSettingEnabled() && !disableSuggestions;
  useTrackRenderCounts('timesheet-labels');
  const classes = useStyles({ loading, classes: overrideClasses });
  const labels = useMemo(() => {
    return mapFormFieldsToTimesheetLabels(fields);
  }, [fields]);
  const {
    timesheetLabels: timesheetLabelsFormField
  } = useLabelsFormFieldContext();
  const commentLabel = filterLabelsByType(labels, LabelType.Comment).pop();
  if (!loading && !labels.length) {
    return <></>;
  }

  return (
    <div
      data-testid={'boundaryContainerTimesheetLabels'}
      className={classes.container}
    >
      <div data-testid="headerContainer" className={classes.headerContainer}>
        <Typography
          variant="h6"
          className={classnames(classes.title, {
            [classes.blueTitle]: isSmartSuggestionEnabled
          })}
          data-testid="timesheet-label-title-text"
        >
          <FormattedMessage id={'timesheetLabels.title'} />
        </Typography>
        {isSmartSuggestionEnabled ? (
          <TimeSheetMoreAction fieldName={timesheetLabelsFormField} />
        ) : (
          <></>
        )}
      </div>
      {loading && (
        <Grid container data-testid="label-loading-progress">
          {Array.from(new Array(3)).map((item, index) => (
            <Skeleton
              key={index}
              className={classes.labelSkeleton}
              width={'8%'}
            />
          ))}
        </Grid>
      )}
      <div data-testid="labelsContainer" className={classes.labelsContainer}>
        {fields.map((field, index) => {
          const label = field.label;
          return (
            !isLabelType(label, LabelType.Comment) && (
              <TimesheetLabel
                label={label}
                key={label.id}
                index={index}
                attributes={attributes}
                onLabelChange={onLabelChange}
              />
            )
          );
        })}
      </div>
      <div
        data-testid="commentLabelContainer"
        className={classes.commentLabelContainer}
      >
        {commentLabel && variant && variant !== 'multi-edit' && (
          <TimesheetCommentLabel
            label={commentLabel as Label}
            index={labels?.indexOf(commentLabel)}
            attributes={attributes}
          />
        )}
      </div>
    </div>
  );
};

export const TimesheetLabels = React.memo(TimesheetLabelsFC);
